import axios, { AxiosError } from 'axios';

/**
 * Extracts a human-readable error message from an axios error whose response
 * body is a Blob (as happens when the request uses `responseType: 'blob'` but
 * the server returns a JSON error).
 *
 * Order of precedence for the extracted message:
 *   1. `title` from ASP.NET `ProblemDetails` / `ValidationProblemDetails`;
 *   2. `detail` from the same contract;
 *   3. first value of `errors` (validation dictionary);
 *   4. raw text body;
 *   5. `error.message`.
 */
export const extractBlobError = async (
  error: unknown,
  fallback: string,
): Promise<string> => {
  if (!axios.isAxiosError(error)) {
    return error instanceof Error ? error.message : fallback;
  }
  const axiosError = error as AxiosError<unknown>;
  const data = axiosError.response?.data;

  if (data instanceof Blob) {
    try {
      const text = await data.text();
      if (!text) return axiosError.message || fallback;

      try {
        const parsed: unknown = JSON.parse(text);
        if (parsed && typeof parsed === 'object') {
          const obj = parsed as {
            title?: string;
            detail?: string;
            errors?: Record<string, string[] | string>;
          };
          if (typeof obj.title === 'string' && obj.title) return obj.title;
          if (typeof obj.detail === 'string' && obj.detail) return obj.detail;
          if (obj.errors && typeof obj.errors === 'object') {
            const first = Object.values(obj.errors)[0];
            if (Array.isArray(first) && first.length > 0) return first[0];
            if (typeof first === 'string') return first;
          }
        }
        return text;
      } catch {
        return text;
      }
    } catch {
      return axiosError.message || fallback;
    }
  }

  return axiosError.message || fallback;
};

/**
 * Extracts the filename from a `Content-Disposition` header, supporting both
 * the legacy `filename="..."` form and RFC 5987 `filename*=UTF-8''...` with
 * percent-encoded non-ASCII (commonly returned for Russian filenames).
 */
const isSafeFileName = (fileName: string | undefined): fileName is string =>
  Boolean(
    fileName?.trim() &&
      !/[\\/]/.test(fileName) &&
      !Array.from(fileName).some((character) => {
        const code = character.charCodeAt(0);
        return code < 32 || code === 127;
      }) &&
      fileName !== '.' &&
      fileName !== '..',
  );

export const extractFileName = (
  contentDisposition: unknown,
  fallbackFileName?: string,
): string => {
  const invalidName = new Error(
    'Сервер не передал корректное имя файла в Content-Disposition.',
  );
  const safeFallback = isSafeFileName(fallbackFileName)
    ? fallbackFileName.trim()
    : undefined;
  if (typeof contentDisposition !== 'string') {
    if (safeFallback) return safeFallback;
    throw invalidName;
  }

  const parameters = new Map<string, string>();
  const pattern =
    /;\s*([^=;\s]+)\s*=\s*(?:"((?:[^"\\]|\\.)*)"|([^";]*))\s*(?=;|$)/gy;
  let position = contentDisposition.indexOf(';');
  while (position >= 0 && position < contentDisposition.length) {
    pattern.lastIndex = position;
    const match = pattern.exec(contentDisposition);
    if (!match) {
      if (safeFallback) return safeFallback;
      throw invalidName;
    }
    parameters.set(
      match[1].toLowerCase(),
      match[2] !== undefined
        ? match[2].replace(/\\(.)/g, '$1')
        : match[3].trim(),
    );
    position = pattern.lastIndex;
  }

  let fileName = parameters.get('filename');
  const extended = parameters.get('filename*');
  const encoded = extended?.match(/^UTF-8'[^']*'(.*)$/i);
  if (encoded) {
    try {
      fileName = decodeURIComponent(encoded[1]);
    } catch (error) {
      if (!(error instanceof URIError)) throw error;
      // A malformed extended value may still have a valid server filename.
    }
  }

  if (isSafeFileName(fileName)) return fileName.trim();
  if (safeFallback) return safeFallback;
  throw invalidName;
};

export const resolveXlsxFileName = (
  contentDisposition: unknown,
  fallbackFileName: string,
): string => {
  const fileName = extractFileName(contentDisposition, fallbackFileName);
  return /\.xlsx$/i.test(fileName) ? fileName : `${fileName}.xlsx`;
};

/**
 * Triggers a browser file download from a Blob payload.
 */
export const triggerBlobDownload = (blob: Blob, fileName: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
